import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, GridSize } from '../pages/CardBrowser';
import { useManaSymbols } from '../hooks/useManaSymbols';
import './CardGrid.css';
import { ContextMenuItem } from './ContextMenu';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

interface Props {
  cards: Card[];
  gridSize: GridSize;
  onCardContextMenu?: (card: Card, e: React.MouseEvent) => void;
  suppressHover?: boolean;
}

const RARITY_COLORS: Record<string, string> = {
  mythic:   '#e8954a',
  rare:     '#c8a45a',
  uncommon: '#8ab4c8',
  common:   '#9a9aaa',
};

const RARITY_LABELS: Record<string, string> = {
  mythic: 'M', rare: 'R', uncommon: 'U', common: 'C',
};

const DFC_LAYOUTS = [
  'transform', 'modal_dfc', 'double_faced_token',
  'art_series', 'reversible_card',
];

const GRID_CONFIG: Record<GridSize, { colWidth: string; popupWidth: number }> = {
  sm: { colWidth: '140px', popupWidth: 280 },
  md: { colWidth: '185px', popupWidth: 320 },
  lg: { colWidth: '220px', popupWidth: 380 },
  xl: { colWidth: '300px', popupWidth: 480 },
};

// ── Frame effect badge config ──────────────────────────────────────────────
export const FRAME_EFFECT_BADGES: Record<string, { label: string; className: string }> = {
  extendedart:  { label: 'Extended Art',   className: 'badge-extendedart' },
  etched:       { label: 'Etched',         className: 'badge-etched' },
  showcase:     { label: 'Showcase',       className: 'badge-showcase' },
  inverted:     { label: 'Inverted',       className: 'badge-inverted' },
  colorshifted: { label: 'Colorshifted',   className: 'badge-colorshifted' },
  shatteredglass: { label: 'Shattered Glass', className: 'badge-shatteredglass' },
  fullart:      { label: 'Full Art',       className: 'badge-fullart' },
};

// ── Promo type badge config ────────────────────────────────────────────────
export const PROMO_TYPE_BADGES: Record<string, { label: string; className: string }> = {
  surgefoil:      { label: 'Surge Foil',       className: 'badge-surgefoil' },
  galaxyfoil:     { label: 'Galaxy Foil',      className: 'badge-galaxyfoil' },
  textured:       { label: 'Textured Foil',    className: 'badge-textured' },
  halofoil:       { label: 'Halo Foil',        className: 'badge-halofoil' },
  ripplefoil:     { label: 'Ripple Foil',      className: 'badge-ripplefoil' },
  rainbowfoil:    { label: 'Rainbow Foil',     className: 'badge-rainbowfoil' },
  doublerainbow:  { label: 'Double Rainbow',   className: 'badge-doublerainbow' },
  oilslick:       { label: 'Oil Slick',        className: 'badge-oilslick' },
  neonink:        { label: 'Neon Ink',         className: 'badge-neonink' },
  serialized:     { label: 'Serialized',       className: 'badge-serialized' },
  gilded:         { label: 'Gilded',           className: 'badge-gilded' },
  raisedfoil:     { label: 'Raised Foil',      className: 'badge-raisedfoil' },
  stepandcompleat:{ label: 'Step & Compleat',  className: 'badge-stepandcompleat' },
  confettifoil:   { label: 'Confetti Foil',    className: 'badge-confettifoil' },
  silverfoil:     { label: 'Silver Foil',      className: 'badge-silverfoil' },
  fracturefoil:   { label: 'Fracture Foil',    className: 'badge-fracturefoil' },
  manafoil:       { label: 'Mana Foil',        className: 'badge-manafoil' },
  invisibleink:   { label: 'Invisible Ink',    className: 'badge-invisibleink' },
  dazzlefoil:     { label: 'Dazzle Foil',      className: 'badge-dazzlefoil' },
  embossed:       { label: 'Embossed',         className: 'badge-embossed' },
  magnified:      { label: 'Magnified',        className: 'badge-magnified' },
};

// Borderless comes from borderColor, not frameEffects
export function getCardBadges(card: {
  borderColor?: string;
  frameEffects?: string[];
  finishes?: string[];
  promoTypes?: string[];
}): { label: string; className: string }[] {
  const badges: { label: string; className: string }[] = [];
  if (card.borderColor === 'borderless') {
    badges.push({ label: 'Borderless', className: 'badge-borderless' });
  }
  if (card.frameEffects) {
    for (const fe of card.frameEffects) {
      const cfg = FRAME_EFFECT_BADGES[fe.trim()];
      if (cfg) badges.push(cfg);
    }
  }
  // Foil-only badge — when finishes has foil but no nonfoil
  if (card.finishes && card.finishes.includes('foil') && !card.finishes.includes('nonfoil')) {
    badges.push({ label: 'Foil Only', className: 'badge-foilonly' });
  }
  // Promo type badges
  if (card.promoTypes) {
    for (const pt of card.promoTypes) {
      const cfg = PROMO_TYPE_BADGES[pt.trim()];
      if (cfg) badges.push(cfg);
    }
  }
  return badges;
}

export function FrameEffectBadges({ card }: {
  card: { borderColor?: string; frameEffects?: string[]; finishes?: string[]; promoTypes?: string[] };
}) {
  const badges = getCardBadges(card);
  if (!badges.length) return null;
  return (
    <div className="badge-row">
      {badges.map((b, i) => (
        <span key={i} className={`frame-badge ${b.className}`}>{b.label}</span>
      ))}
    </div>
  );
}

function ColorPips({ colors }: { colors: string }) {
  const symbols = useManaSymbols();
  if (!colors) return null;
  return (
    <div className="color-pips">
      {colors.split(',').filter(Boolean).map(c => {
        const token = `{${c}}`;
        const sym = symbols[token];
        if (sym?.imageUrl) {
          return (
            <img
              key={c}
              src={sym.imageUrl}
              alt={sym.english ?? c}
              title={sym.english ?? c}
              width={14}
              height={14}
              className="color-pip-img"
            />
          );
        }
        const dotColors: Record<string, string> = {
          W: '#f5f0e0', U: '#4a90d9', B: '#8a6a9a', R: '#e05a4a', G: '#4a9a6a',
        };
        return (
          <span key={c} className="color-pip"
            style={{ background: dotColors[c] || '#888' }} title={c} />
        );
      })}
    </div>
  );
}

function getTileImage(card: Card, gridSize: GridSize, flipped: boolean): string | null {
  const useLarge = gridSize === 'lg' || gridSize === 'xl';
  if (flipped) {
    return useLarge
      ? (card.backImageUrl   || card.backImageSmall || card.backImageLarge || null)
      : (card.backImageSmall || card.backImageUrl   || card.backImageLarge || null);
  }
  return useLarge
    ? (card.imageUrl   || card.imageSmall || card.imageLarge || null)
    : (card.imageSmall || card.imageUrl   || card.imageLarge || null);
}

function getPopupImage(card: Card, gridSize: GridSize, flipped: boolean): string | null {
  const useLarge = gridSize === 'lg' || gridSize === 'xl';
  if (flipped) {
    return useLarge
      ? (card.backImageLarge || card.backImageUrl   || card.backImageSmall || null)
      : (card.backImageUrl   || card.backImageLarge || card.backImageSmall || null);
  }
  return useLarge
    ? (card.imageLarge || card.imageUrl   || card.imageSmall || null)
    : (card.imageUrl   || card.imageLarge || card.imageSmall || null);
}

function CardTile({ card: initialCard, gridSize, onCardContextMenu, suppressHover }: {
  card: Card; gridSize: GridSize;
  onCardContextMenu?: (card: Card, e: React.MouseEvent) => void;
  suppressHover?: boolean;
}) {
  const [card,        setCard]        = useState<Card>(initialCard);
  const [flipped,     setFlipped]     = useState(false);
  const [hoverPos,    setHoverPos]    = useState<{ x: number; y: number } | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [dlResult,    setDlResult]    = useState<'success' | 'error' | null>(null);
  const navigate = useNavigate();

  const { popupWidth } = GRID_CONFIG[gridSize];
  const isDFC       = DFC_LAYOUTS.includes(card.layoutCode);
  const tileImg     = getTileImage(card, gridSize, flipped);
  const popupImg    = getPopupImage(card, gridSize, flipped);
  const hasImage    = !!tileImg;
  const rarityColor = RARITY_COLORS[card.rarity] || '#888';

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setDownloading(true);
    setDlResult(null);
    try {
      const res = await axios.post(`${API}/images/download/${card.cardId}`);
      if (res.data.downloaded > 0 || res.data.skipped > 0) {
        try {
          const refreshed = await axios.get(`${API}/cards/${card.cardId}`);
          if (refreshed.data) { setCard(refreshed.data); setFlipped(false); }
        } catch { /* not critical */ }
        setDlResult('success');
      } else {
        setDlResult('error');
      }
    } catch {
      setDlResult('error');
    } finally {
      setDownloading(false);
      setTimeout(() => setDlResult(null), 3000);
    }
  };

  const handleFlip = (e: React.MouseEvent | React.PointerEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setFlipped(prev => !prev);
  };

  return (
    <>
      <div
        className="card-tile"
        onClick={() => navigate(`/cards/${card.cardId}`, {
          state: { from: window.location.pathname + window.location.search }
        })}
        onContextMenu={e => {
          if (onCardContextMenu) {
            e.preventDefault();
            setHoverPos(null);  // clear hover popup before opening context menu
            onCardContextMenu(card, e);
          }
        }}
        onMouseEnter={e => setHoverPos({ x: e.clientX, y: e.clientY })}
        onMouseMove={e  => setHoverPos({ x: e.clientX, y: e.clientY })}
        onMouseLeave={()  => setHoverPos(null)}
      >
        <div className="card-image-wrap">
          {hasImage ? (
            <img src={tileImg!} alt={card.name} className="card-image" loading="lazy" />
          ) : (
            <div className="card-image-placeholder">
              <div className="placeholder-icon">&#11042;</div>
              <div className="placeholder-name">{card.name}</div>
              <button
                className={`download-btn ${downloading ? 'loading' : ''} ${dlResult || ''}`}
                onClick={handleDownload}
                disabled={downloading}
                title="Download card image from Scryfall"
              >
                {downloading             ? <span className="dl-spinner" />
                : dlResult === 'success' ? '✓ Done'
                : dlResult === 'error'   ? '✗ Failed'
                : '⬇ Download'}
              </button>
            </div>
          )}

          {isDFC && (card.backImageSmall || card.backImageUrl || card.backImageLarge) && (
            <button
              className="flip-btn"
              onPointerDown={handleFlip}
              onClick={e => { e.stopPropagation(); e.preventDefault(); }}
              title={flipped ? 'Show front face' : 'Show back face'}
            >
              ↺
            </button>
          )}
        </div>

        <div className="card-info">
          <div className="card-name" title={card.name}>{card.name}</div>
          <div className="card-meta-row">
            <span className="card-set" title={card.setName}>{card.setCode?.toUpperCase()}</span>
            <ColorPips colors={card.colors} />
            <span className="rarity-gem" style={{ background: rarityColor }} title={card.rarity}>
              {RARITY_LABELS[card.rarity] || '?'}
            </span>
            {card.qtyOwned > 0 && <span className="owned-count">×{card.qtyOwned}</span>}
            {card.isFoilOwned && (
              <span className="owned-foil-badge">FOIL</span>
            )}
            {card.price > 0 && <span className="card-price">${card.price.toFixed(2)}</span>}
            {!card.hasLocalImage && hasImage && (
              <span className="cdn-badge" title="Image from Scryfall CDN">CDN</span>
            )}
          </div>
          {/* Variant badges — inline wrapping row */}
          <FrameEffectBadges card={card} />
        </div>
      </div>

      {hoverPos && popupImg && !suppressHover && (
        <HoverPopup imageUrl={popupImg} x={hoverPos.x} y={hoverPos.y} width={popupWidth} />
      )}
    </>
  );
}

function HoverPopup({ imageUrl, x, y, width }: {
  imageUrl: string; x: number; y: number; width: number;
}) {
  const showLeft = x > window.innerWidth * 0.6;
  const top = Math.min(
    Math.max(y - 80, 10),
    window.innerHeight - Math.round(width * 1.4) - 10
  );
  return (
    <div
      className="hover-popup"
      style={{
        position: 'fixed',
        top,
        left: showLeft ? x - width - 16 : x + 20,
        width,
        zIndex: 9999,
        pointerEvents: 'none',
      }}
    >
      <img src={imageUrl} alt="" className="hover-popup-img" />
    </div>
  );
}

export default function CardGrid({ cards, gridSize, onCardContextMenu, suppressHover }: Props) {
  const { colWidth } = GRID_CONFIG[gridSize];

  if (cards.length === 0) {
    return (
      <div className="card-grid-empty">
        <div className="empty-icon">&#11042;</div>
        <div className="empty-text">No cards found</div>
        <div className="empty-sub">Try adjusting your search or filters</div>
      </div>
    );
  }

  return (
    <div
      className="card-grid"
      style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${colWidth}, 1fr))` }}
    >
      {cards.map(card => (
        <CardTile
          key={`${card.cardId}-${card.isFoilOwned ? 'foil' : 'reg'}`}
          card={card}
          gridSize={gridSize}
          onCardContextMenu={onCardContextMenu}
          suppressHover={suppressHover}
        />
      ))}
    </div>
  );
}
